import {
  IVideo,
  IStyleOption,
  TVideoMode,
  IAiVideoModel
} from '../context/interfaces'

interface ICSVRow {
  prompt: string
  style: string
  tts: string
  model: string
  mode: string
}

interface ICSVParseResult {
  videos: IVideo[]
  errors: string[]
  warnings: string[]
}

const parseCSVLine = (line: string): string[] => {
  const result: string[] = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    if (char === '"') {
      inQuotes = !inQuotes
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim())
      current = ''
    } else {
      current += char
    }
  }
  result.push(current.trim())
  return result
}

const removeQuotes = (str: string): string => {
  return str.replace(/^["']|["']$/g, '')
}

const isValidMode = (mode: string): mode is TVideoMode => {
  return mode === 'animated' || mode === 'storyboard'
}

const findStyleIdByName = (
  styleName: string,
  styles: IStyleOption[]
): number | null => {
  const normalizedName = styleName.toLowerCase().trim()
  const style = styles.find(
    s => s.label.toLowerCase().trim() === normalizedName
  )
  return style ? style.id : null
}

const parseTtsValue = (tts: string): boolean => {
  const normalized = tts.toLowerCase().trim()
  return normalized !== 'yes' // inverse logic: 'yes' -> isVoiceOverDisabled = false
}

const findModelCodeByName = (
  modelName: string,
  models: IAiVideoModel[]
): string | undefined => {
  const normalized = modelName.toLowerCase().trim()
  const model = models.find(m => m.name.toLowerCase().trim() === normalized)
  return model?.model
}

const createVideoFromCSVRow = (
  row: ICSVRow,
  styles: IStyleOption[],
  models: IAiVideoModel[],
  defaultStyleId: number
): IVideo => {
  const mode = isValidMode(row.mode.toLowerCase())
    ? (row.mode.toLowerCase() as TVideoMode)
    : 'animated'

  const styleId = findStyleIdByName(row.style, styles) ?? defaultStyleId
  const isVoiceOverDisabled = parseTtsValue(row.tts)

  // Model logic: only use for animated mode
  let model: string | undefined = undefined
  if (mode === 'animated') {
    // Try to find model by name/code, or use first available model as default
    model = findModelCodeByName(row.model, models) || models[0]?.model
  }

  return {
    id: `video-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    mode,
    generateVideoBody: {
      prompt: row.prompt,
      styleId,
      isVoiceOverDisabled,
      model
    }
  }
}

export const parseCSVFile = async (
  file: File,
  styles: IStyleOption[],
  models: IAiVideoModel[]
): Promise<ICSVParseResult> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (event: ProgressEvent<FileReader>) => {
      try {
        const text = event.target?.result as string
        if (!text) {
          reject(new Error('Empty file'))
          return
        }

        const lines = text.split('\n').filter(line => line.trim())
        if (lines.length < 2) {
          reject(
            new Error(
              'CSV file must contain a header row and at least one data row'
            )
          )
          return
        }

        const videos: IVideo[] = []
        const errors: string[] = []
        const warnings: string[] = []

        // Parse header
        const headers = parseCSVLine(lines[0]).map(h =>
          removeQuotes(h).toLowerCase()
        )

        // Validate headers
        const requiredHeaders = ['prompt', 'style', 'tts', 'model', 'mode']
        const missingHeaders = requiredHeaders.filter(h => !headers.includes(h))
        if (missingHeaders.length > 0) {
          reject(
            new Error(`CSV must contain headers: ${requiredHeaders.join(', ')}`)
          )
          return
        }

        const promptIndex = headers.indexOf('prompt')
        const styleIndex = headers.indexOf('style')
        const ttsIndex = headers.indexOf('tts')
        const modelIndex = headers.indexOf('model')
        const modeIndex = headers.indexOf('mode')

        const defaultStyleId = styles.length > 0 ? styles[0].id : 1

        // Parse data rows
        for (let i = 1; i < lines.length; i++) {
          const values = parseCSVLine(lines[i])

          // Skip empty lines
          if (values.every(v => !v)) {
            continue
          }

          // Validate row has enough columns
          if (values.length < 5) {
            warnings.push(`Row ${i + 1}: Skipped - insufficient columns`)
            continue
          }

          const prompt = removeQuotes(values[promptIndex])
          const style = removeQuotes(values[styleIndex])
          const tts = removeQuotes(values[ttsIndex])
          const model = removeQuotes(values[modelIndex])
          const mode = removeQuotes(values[modeIndex])

          // Validate required fields
          if (!prompt || !prompt.trim()) {
            warnings.push(`Row ${i + 1}: Skipped - missing prompt`)
            continue
          }

          if (!style || !style.trim()) {
            warnings.push(
              `Row ${i + 1}: Using default style - style not specified`
            )
          }

          if (!tts || !tts.trim()) {
            warnings.push(
              `Row ${i + 1}: Using default value (no) for tts - tts not specified`
            )
          }

          const trimmedMode = mode.trim().toLowerCase()
          if (!mode || !mode.trim()) {
            warnings.push(
              `Row ${i + 1}: Using default mode (animated) - mode not specified`
            )
          }

          if (mode && !isValidMode(trimmedMode)) {
            warnings.push(
              `Row ${i + 1}: Invalid mode "${mode}" - using default mode (animated)`
            )
          }

          // Check model for animated mode
          if (
            trimmedMode === 'animated' ||
            !mode ||
            !isValidMode(trimmedMode)
          ) {
            if (!model || !model.trim()) {
              warnings.push(
                `Row ${i + 1}: Using default model - model not specified for animated mode`
              )
            } else if (!findModelCodeByName(model, models)) {
              warnings.push(
                `Row ${i + 1}: Model "${model}" not found - using default model`
              )
            }
          }

          // Check if style exists
          if (style && findStyleIdByName(style, styles) === null) {
            warnings.push(
              `Row ${i + 1}: Style "${style}" not found - using default style`
            )
          }

          const csvRow: ICSVRow = {
            prompt: prompt.trim(),
            style: style.trim(),
            tts: tts.trim() || 'no',
            model: model.trim(),
            mode: mode.trim() || 'animated'
          }

          videos.push(
            createVideoFromCSVRow(csvRow, styles, models, defaultStyleId)
          )
        }

        if (videos.length === 0) {
          reject(new Error('No valid videos found in CSV file'))
          return
        }

        resolve({ videos, errors, warnings })
      } catch (error) {
        reject(
          error instanceof Error ? error : new Error('Failed to parse CSV file')
        )
      }
    }

    reader.onerror = () => {
      reject(new Error('Failed to read file'))
    }

    reader.readAsText(file)
  })
}
